import React, {Key, RefObject} from 'react';
import { FormInstance, Select, TreeSelect } from "antd";
import { connect } from "react-redux";
import IContentType from "../../../model/interface/dataStorage/IContentType";
import selectors from "../../../redux/selectors";
import IField from "../../../model/interface/dataStorage/IField";
import IRepositoryService from "../../../model/interface/IRepositoryService";
import Utils from "../../../utils";
import FieldsService from "../../../model/service/dataStorage/FieldsService";
import { IAppState } from "../../../redux/store";
import ReactDOMServer from "react-dom/server";
import ViewTableItemsSettings from "../../app/view/table/ViewTableItemsSettings";
import LocaleText from "../../app/settings/dictionary/LocaleText";

// Define the shape of a TreeSelect Option
interface TreeOption {
    key?: Key;
    title?: React.ReactNode;
    value?: Key;
    children?: TreeOption[];
    field?: IField;
    disabled?: boolean;
    isLeaf?: boolean;
    level?: number
}

export type FieldPickerMode = 'simple' | 'tree'

interface IProps {
    value?: string | string[]
    onChange?: (identifier?: string | string[], field?: IField | IField[]) => void
    className?: string,
    style?: React.CSSProperties,
    mode?: FieldPickerMode
    output: 'name' | 'uuid'
    allowTypes?: string[]
    showDisabledTypes?: boolean
    forbiddenTypes?: string[]
    disabled?: boolean
    serviceClassName: string | 'all'
    contentTypes: IContentType[]
    findServiceByClassName: (className: string) => IRepositoryService | null
    multiple?: boolean
    namePrefix?: string,
    allowTargetEntity?: string[]
    includeIdentifierFields?: boolean,
    getText: (code: string, fallBack?: string) => string
    includedInChangeList?: boolean
    maxDepth?: number
    ignoreFields?: string[]
    defaultActiveFirstOption?: boolean
    autoReset?: boolean
}

interface IState {
    formRef: RefObject<FormInstance>,
    showModal: boolean,
    treeData: TreeOption[] // 👈 New state for managing dynamic tree data
}

class FieldPicker extends React.Component<IProps, IState> {

    static defaultProps = {
        mode: 'simple' as FieldPickerMode,
        multiple: false
    }


    constructor(props: IProps) {
        super(props);
        this.state = {
            formRef: React.createRef(),
            showModal: false,
            treeData: [], // Initialize with empty
        }
    }

    componentDidMount() {
        this.initializeTreeData(this.props);
    }

    componentDidUpdate(prevProps: IProps) {
        if (prevProps.serviceClassName !== this.props.serviceClassName) {
            this.initializeTreeData(this.props);
        }
    }

    initializeTreeData = (props: IProps) => {
        if (props.mode === 'tree') {
            const fields = this.getFields(props.serviceClassName);
            const initialTreeData = this.getTreeOptions(fields, 0, '', this.getTreeDefaultExpandedKeys(this.getParsedValue()));
            this.setState({ treeData: initialTreeData });
        }
    }

    onPickerChoose = (identifier?: string | string[], field?: IField | IField[]) => {
        const { onChange, namePrefix } = this.props
        let output = identifier
        if (namePrefix) {
            if (Array.isArray(output)) {
                output = output.map(o => namePrefix + o)
            } else {
                output = output ? namePrefix + output : output
            }
        }
        onChange?.(output, field)
    }

    /**
     * Helper to recursively find a node and update its children.
     */
    updateTreeData = (list: TreeOption[], key: Key, children: TreeOption[]): TreeOption[] => {
        return list.map(node => {
            if (node.key === key) {
                return { ...node, children };
            }
            if (node.children) {
                return {
                    ...node,
                    children: this.updateTreeData(node.children, key, children),
                };
            }
            return node;
        });
    };

    /**
     * The core AntD function for dynamic loading of children.
     */
    onLoadData = (treeNode: TreeOption): Promise<void> => {
        return new Promise(resolve => {
            if (treeNode.children?.length || !treeNode.field?.targetEntity) {
                resolve();
                return;
            }

            const targetEntity = treeNode.field.targetEntity;
            const newFields = this.getFields(targetEntity);

            const newChildren = treeNode.key && this.getTreeOptions(newFields, 1, treeNode.key, [], (treeNode.level || 0) + 1);

            treeNode.key && this.setState(prevState => ({
                treeData: this.updateTreeData(prevState.treeData, treeNode.key || '', newChildren || []),
            }), resolve);
        });
    };

    getTreeOptions = (fields: IField[], depth: number, prevField?: Key, preloadKeys?: string[], level: number = 1): TreeOption[] => {
        const { output, includeIdentifierFields, getText, maxDepth } = this.props
        const options: TreeOption[] = []
        const prefix = (prevField ? (prevField + '.') : '')

        if (output === 'name' && includeIdentifierFields) {
            options.push({ title: 'ID', value: prefix + '.id', key: prefix + '.id', level: 1 })
            options.push({ title: 'UUID', value: prefix + '.uuid', key: prefix + '.uuid', level: 1 })
        }

        fields.forEach(field => {
            const key = output === "uuid" ? field.uuid : prefix + field.name
            if (preloadKeys?.includes(key)){
                depth = 1
            }
            switch (field.mode) {
                case ("scalar"):
                    options.push({
                        title: getText(field.label || field.name),
                        value: key,
                        key: key,
                        field: field,
                        disabled: !this.isFieldAllowed(field),
                        isLeaf: true, // 👈 Must be set for non-expandable nodes
                        level
                    })
                    break;
                case ("relation"):
                    const isExpandable = field.targetEntity && (!maxDepth || level <= maxDepth)
                    let children: TreeOption[] | undefined;

                    if (isExpandable) {
                        if (depth > 0 && field.targetEntity) {
                            // Recursively load children, reducing depth
                            children = this.getTreeOptions(this.getFields(field.targetEntity), depth - 1, key, preloadKeys, level + 1)
                        } else {
                            children = undefined;
                        }
                    }

                    options.push({
                        title: getText(field.label || field.name),
                        value: key,
                        key: key,
                        children: children,
                        field: field,
                        disabled: !this.isFieldAllowed(field),
                        isLeaf: !isExpandable, // 👈 Set to false if it can be expanded/loaded
                        level
                    })
                    break;
            }
        })
        return options
    }

    render() {
        const {
            className,
            disabled,
            mode,
            output,
            multiple,
            serviceClassName,
            defaultActiveFirstOption,
            autoReset,
        } = this.props

        const { treeData } = this.state;

        const fields = this.getFields(serviceClassName);

        let groupedFields = serviceClassName === 'all' ? this.props.contentTypes.map(contentType => (
            { name: contentType.name, label: <LocaleText code={contentType.label || contentType.name}/>, fields: this.getFields(contentType.fullClassName) }
        )) : []
        let parsedValue = this.getParsedValue();

        return {
            simple:
                <Select allowClear={true} disabled={disabled} className={className}
                        dropdownMatchSelectWidth={false}
                        defaultActiveFirstOption={defaultActiveFirstOption}
                        mode={multiple ? "multiple" : undefined}
                        value={parsedValue}
                        showSearch={true}
                        filterOption={(input, option) =>
                            Utils.stringContains(ReactDOMServer.renderToString(option?.children), input)}
                        onChange={value => this.onPickerChoose(value, fields.find(f => f[output] === value))}>
                    {groupedFields.length ?
                        groupedFields.map(group => (
                            <Select.OptGroup key={group.name} label={group.label}>
                                {this.buildFieldOption(group.fields, output)}
                            </Select.OptGroup>
                        ))
                        : this.buildFieldOption(fields, output)}
                </Select>,
            tree:
                <TreeSelect
                    // Added loadData for dynamic loading
                    loadData={this.onLoadData}
                    treeDefaultExpandedKeys={this.getTreeDefaultExpandedKeys(parsedValue)}
                    value={parsedValue}
                    allowClear
                    key={autoReset ? Utils.uuid() : 'tree'}
                    showSearch={true}
                    filterTreeNode={(input, node) => Utils.stringContains(node?.title, input)}
                    multiple={!!multiple}
                    disabled={disabled}
                    className={className}
                    // Use the data from the component state
                    treeData={treeData}
                    onChange={(value, node, extra) => {
                        // Use the field from the first node/trigger node if multiple is false
                        const field = multiple ? undefined : extra?.triggerNode?.props?.field;
                        this.onPickerChoose(value, field)
                    }}
                />
        }[mode || 'simple']
    }

    private getParsedValue() {
        const {
            value,
            multiple,
            namePrefix,
        } = this.props
        let parsedValue = value
        if (multiple && !Array.isArray(parsedValue)) {
            parsedValue = parsedValue ? [parsedValue] : []
        } else if (!multiple && Array.isArray(parsedValue)) {
            parsedValue = parsedValue[0]
        }
        if (namePrefix) {
            parsedValue = Array.isArray(value) ?
                value.map(v => v.replace(namePrefix, '')) : value?.replace(namePrefix, '')
        }
        return parsedValue;
    }

    private getTreeDefaultExpandedKeys(parsedValue: string[] | undefined | string) {
        return parsedValue ? Array.isArray(parsedValue) ? parsedValue : parsedValue.split('.').slice(0, -1).reduce((accumulator: string[], currentPart) => {
            // Determine the next prefix
            const nextPrefix = accumulator.length === 0
                ? currentPart
                : `${accumulator[accumulator.length - 1]}.${currentPart}`;

            // Add the next prefix to the accumulator and return it
            accumulator.push(nextPrefix);
            return accumulator;
        }, []) : [];
    }

    private buildFieldOption(fields: IField[], output: "name" | "uuid") {
        const { showDisabledTypes, getText } = this.props
        return fields.filter(f => showDisabledTypes || this.isFieldAllowed(f)).map(field => (
            <Select.Option key={field.id} value={{ uuid: field.uuid, name: field.name }[output]}
                           disabled={ViewTableItemsSettings.isFilterableDisabled(field)}>
                {getText(field.label || field.name)}
                {field.label && <div className={'ml-2 d-inline text-muted'}>{field.name}</div>}
            </Select.Option>
        ));
    }

    getFields(serviceClassName: string) {
        const { findServiceByClassName, showDisabledTypes, getText, includedInChangeList, ignoreFields } = this.props
        return FieldsService.sort(findServiceByClassName(serviceClassName)?.getFields() || [])
            .filter(f => showDisabledTypes || this.isFieldAllowed(f))
            .filter(f => !includedInChangeList || f.includeInChangeList)
            .filter(f => !ignoreFields?.includes(f.uuid))
            .sort((a, b) => {
                const aLabel = getText(a.label || a.name).toLowerCase()
                const bLabel = getText(b.label || b.name).toLowerCase()
                return aLabel > bLabel ? 1 : (bLabel > aLabel ? -1 : 0)
            })
    }

    isFieldAllowed = (f: IField) => {
        const { allowTypes, allowTargetEntity, forbiddenTypes } = this.props
        return (!(forbiddenTypes?.length) || !forbiddenTypes.includes(f.type))
            && (!(allowTypes?.length) || allowTypes.includes(f.type))
            && (!(allowTargetEntity?.length) || (f.targetEntity && allowTargetEntity.includes(f.targetEntity)))
    }
}

const mapStateToProps = (state: IAppState) => {
    const { contentTypes } = state.setup
    return {
        contentTypes,
        findServiceByClassName: (className: string) => selectors.services.findOneOrNullByFullClassName(state, className),
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    }
}

export default connect(mapStateToProps)(FieldPicker)